import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import { MAX_SELECTED_CURVES, MAX_SELECTED_SYMBOLS } from '@/config/constants';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import { logout } from '@/store/authActions';

import { SELECTOR_MODES } from '../constants';
import {
  applyToggleChanges,
  buildKeySet,
  computeToggleChanges,
  filterByKey
} from '../lib/selectionHelpers';

export { SELECTOR_MODES };

export type SymbolSelectorState = {
  symbols: SymbolKey[];
  previousSymbols: SymbolKey[] | null;
  limitReached: boolean;
  isDashboardAppSelector: boolean;
  selectorMode: SELECTOR_MODES;
  pendingAddAppDrawer: boolean;
  pendingAppSymbols: SymbolKey[] | null;
  /** True while a modal that seeds/reads this selection itself (e.g. the
   * homepage watchlist picker) is open — the floating toolbar renders behind
   * such modals, so it's suppressed rather than left visible-but-unusable. */
  toolbarSuppressed: boolean;
};

const initialState: SymbolSelectorState = {
  symbols: [],
  previousSymbols: null,
  limitReached: false,
  isDashboardAppSelector: false,
  selectorMode: SELECTOR_MODES.NEUTRAL,
  pendingAddAppDrawer: false,
  pendingAppSymbols: null,
  toolbarSuppressed: false
};

type SymbolAction = { symbol: SymbolKey };
type SymbolArrayAction = { symbols: SymbolKey[] };

const updateLimitReached = (state: SymbolSelectorState) => {
  state.limitReached = state.symbols.length >= MAX_SELECTED_SYMBOLS;
};

const inForwardMode = (state: SymbolSelectorState) =>
  state.selectorMode === SELECTOR_MODES.FORWARD_CURVE;

const autoResetMode = (state: SymbolSelectorState) => {
  if (state.symbols.length === 0) {
    state.selectorMode = SELECTOR_MODES.NEUTRAL;
  }
};

const clearSelectionState = (state: SymbolSelectorState) => {
  state.symbols = [];
  state.limitReached = false;
  state.selectorMode = SELECTOR_MODES.NEUTRAL;
};

const symbolSelectorSlice = createSlice({
  name: 'symbolSelector',
  initialState,
  reducers: {
    setSymbols: (state, action: PayloadAction<SymbolKey[]>) => {
      state.symbols = action.payload;
    },

    addSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const keys = buildKeySet(state.symbols, inForwardMode(state));
      if (keys.has(symbolKey(symbol, inForwardMode(state)))) return;
      if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
        return;
      }
      state.symbols = [symbol, ...state.symbols];
    },

    addSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const keys = buildKeySet(state.symbols, inForwardMode(state));
      const toAdd: SymbolKey[] = [];
      for (const s of payload.symbols) {
        const k = symbolKey(s, inForwardMode(state));
        if (keys.has(k)) continue;
        if (state.symbols.length + toAdd.length >= MAX_SELECTED_SYMBOLS) {
          state.limitReached = true;
          break;
        }
        keys.add(k);
        toAdd.push(s);
      }
      if (toAdd.length) {
        state.symbols = [...state.symbols, ...toAdd];
      }
    },

    addForwardCurves: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const existingCurveKeys = new Set(state.symbols.map(s => symbolKey(s, true)));
      const grouped = new Map<string, SymbolKey[]>();
      for (const sym of payload.symbols) {
        const ck = symbolKey(sym, true);
        const group = grouped.get(ck) ?? [];
        group.push(sym);
        grouped.set(ck, group);
      }
      const toAdd: SymbolKey[] = [];
      let curveCount = existingCurveKeys.size;
      for (const [ck, syms] of grouped) {
        if (existingCurveKeys.has(ck)) continue;
        if (curveCount >= MAX_SELECTED_CURVES) {
          state.limitReached = true;
          break;
        }
        toAdd.push(...syms);
        curveCount++;
      }
      if (toAdd.length) {
        state.symbols = [...state.symbols, ...toAdd];
      }
    },

    deleteSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const k = symbolKey(payload.symbol, inForwardMode(state));
      state.symbols = state.symbols.filter(s => symbolKey(s, inForwardMode(state)) !== k);
      updateLimitReached(state);
      autoResetMode(state);
    },

    deleteSymbols: (state, { payload }: PayloadAction<{ symbols: SymbolKey[] }>) => {
      const deleteSet = buildKeySet(payload.symbols, inForwardMode(state));
      state.symbols = filterByKey(state.symbols, deleteSet, inForwardMode(state));
      autoResetMode(state);
    },

    reorderSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.symbols = payload.symbols;
    },

    toggleSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const k = symbolKey(symbol, inForwardMode(state));
      const keys = buildKeySet(state.symbols, inForwardMode(state));
      if (keys.has(k)) {
        state.symbols = state.symbols.filter(s => symbolKey(s, inForwardMode(state)) !== k);
        updateLimitReached(state);
        autoResetMode(state);
      } else if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
      } else {
        state.symbols = [symbol, ...state.symbols];
      }
    },

    toggleSymbolsArray: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const { toRemove, toAdd, limitReached } = computeToggleChanges(
        state.symbols,
        payload.symbols,
        inForwardMode(state),
        MAX_SELECTED_SYMBOLS
      );
      if (limitReached) {
        state.limitReached = true;
      }
      if (toRemove.size || toAdd.length) {
        state.symbols = applyToggleChanges(state.symbols, toRemove, toAdd);
        updateLimitReached(state);
        autoResetMode(state);
      }
    },

    overrideSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.previousSymbols = state.symbols;
      state.symbols = payload.symbols;
      updateLimitReached(state);
    },

    restoreSymbols: state => {
      if (state.previousSymbols !== null) {
        state.symbols = state.previousSymbols;
        state.previousSymbols = null;
        updateLimitReached(state);
      }
    },

    clearSelection: clearSelectionState,

    dismissLimit: state => {
      state.limitReached = false;
    },

    setIsDashboardAppSelector: (state, action: PayloadAction<boolean>) => {
      state.isDashboardAppSelector = action.payload;
    },

    setToolbarSuppressed: (state, action: PayloadAction<boolean>) => {
      state.toolbarSuppressed = action.payload;
    },

    setSelectorMode: (state, action: PayloadAction<SELECTOR_MODES>) => {
      if (action.payload === SELECTOR_MODES.NEUTRAL) {
        state.symbols = [];
        state.selectorMode = SELECTOR_MODES.NEUTRAL;
      } else if (
        state.selectorMode !== SELECTOR_MODES.NEUTRAL &&
        action.payload !== state.selectorMode
      ) {
        return;
      } else {
        state.selectorMode = action.payload;
      }
    },

    requestAddAppDrawer: state => {
      state.pendingAddAppDrawer = true;
    },

    clearPendingAddAppDrawer: state => {
      state.pendingAddAppDrawer = false;
    },

    setPendingAppSymbols: (state, { payload }: PayloadAction<SymbolKey[]>) => {
      state.pendingAppSymbols = payload;
    },

    clearPendingAppSymbols: state => {
      state.pendingAppSymbols = null;
    },

    toggleForwardCurve: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      if (!payload.symbols.length) return;
      const curveKey = symbolKey(payload.symbols[0], true);
      const isSelected = state.symbols.some(s => symbolKey(s, true) === curveKey);

      if (isSelected) {
        state.symbols = state.symbols.filter(s => symbolKey(s, true) !== curveKey);
        const curveCount = new Set(state.symbols.map(s => symbolKey(s, true))).size;
        state.limitReached = curveCount >= MAX_SELECTED_CURVES;
        autoResetMode(state);
      } else {
        const selectedCurveKeys = new Set(state.symbols.map(s => symbolKey(s, true)));
        if (selectedCurveKeys.size >= MAX_SELECTED_CURVES) {
          state.limitReached = true;
          return;
        }
        state.symbols = [...payload.symbols, ...state.symbols];
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(logout, clearSelectionState);
  }
});

export const {
  setSymbols,
  addSymbol,
  addSymbols,
  addForwardCurves,
  deleteSymbol,
  clearSelection,
  dismissLimit,
  deleteSymbols,
  reorderSymbols,
  toggleSymbol,
  toggleSymbolsArray,
  toggleForwardCurve,
  setIsDashboardAppSelector,
  setToolbarSuppressed,
  overrideSymbols,
  restoreSymbols,
  setSelectorMode,
  requestAddAppDrawer,
  clearPendingAddAppDrawer,
  setPendingAppSymbols,
  clearPendingAppSymbols
} = symbolSelectorSlice.actions;

export const requestSpotSelection = createAction<{ symbol: SymbolKey } | { symbols: SymbolKey[] }>(
  'symbolSelector/requestSpotSelection'
);

export const requestForwardCurveSelection = createAction<{ symbols: SymbolKey[] }>(
  'symbolSelector/requestForwardCurveSelection'
);

export const requestAddForwardCurves = createAction<{ symbols: SymbolKey[] }>(
  'symbolSelector/requestAddForwardCurves'
);

export const symbolSelectorReducer = symbolSelectorSlice.reducer;
export default symbolSelectorSlice.reducer;
